import { useState } from 'react'
import { VisitorCounter } from './VisitorCounter'

// WhatsApp business number (no spaces, no +)
const WA_NUMBER = '917691020300'

const initialState = {
  name: '',
  email: '',
  message: '',
  phone: '',
  bill_amount: '',
}

const initialErrors = {
  phone: '',
  email: '',
}

// Indian mobile: 10 digits, starts with 6-9 (optionally prefixed with +91 or 0)
const validatePhone = (val) => {
  const digits = val.replace(/[\s\-().+]/g, '')
  const stripped = digits.startsWith('91') && digits.length === 12 ? digits.slice(2) : digits
  if (!stripped) return 'Phone number is required.'
  if (!/^[6-9]\d{9}$/.test(stripped)) return 'Enter a valid 10-digit Indian mobile number.'
  return ''
}

const validateEmail = (val) => {
  if (!val) return '' // email is optional
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return 'Enter a valid email address.'
  return ''
}

export const Contact = (props) => {
  const [formData, setFormData] = useState(initialState)
  const [errors, setErrors]     = useState(initialErrors)
  const [touched, setTouched]   = useState({ phone: false, email: false })
  const [status, setStatus]     = useState(null)

  const { name, email, message, bill_amount, phone } = formData

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (name === 'phone' && touched.phone)
      setErrors((prev) => ({ ...prev, phone: validatePhone(value) }))
    if (name === 'email' && touched.email)
      setErrors((prev) => ({ ...prev, email: validateEmail(value) }))
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    setTouched((prev) => ({ ...prev, [name]: true }))
    if (name === 'phone') setErrors((prev) => ({ ...prev, phone: validatePhone(value) }))
    if (name === 'email') setErrors((prev) => ({ ...prev, email: validateEmail(value) }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const phoneErr = validatePhone(phone)
    const emailErr = validateEmail(email)
    setErrors({ phone: phoneErr, email: emailErr })
    setTouched({ phone: true, email: true })
    if (phoneErr || emailErr) return

    // Build pre-filled WhatsApp message
    const waText = [
      `🌞 *New Solar Enquiry – NewGen Solar Website*`,
      ``,
      `👤 *Name:* ${name}`,
      `📞 *Phone:* ${phone}`,
      `📧 *Email:* ${email || 'Not provided'}`,
      `⚡ *Monthly Bill:* ₹${bill_amount}`,
      `💬 *Message:* ${message}`,
    ].join('\n')

    const waUrl = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(waText)}`
    window.open(waUrl, '_blank', 'noopener,noreferrer')

    setStatus('success')
    setFormData(initialState)
    setTouched({ phone: false, email: false })
    setErrors(initialErrors)
  }

  return (
    <div>
      {/* ── Contact Section ── */}
      <div id='contact'>
        {/* Top wave divider */}
        <div className='contact-wave'>
          <svg viewBox='0 0 1440 60' preserveAspectRatio='none' xmlns='http://www.w3.org/2000/svg'>
            <path d='M0,30 C360,60 1080,0 1440,30 L1440,0 L0,0 Z' fill='#f6f6f6' />
          </svg>
        </div>

        <div className='container'>
          <div className='row'>
            {/* ── Section heading ── */}
            <div className='col-md-10 col-md-offset-1 text-center contact-heading'>
              <h2>Get In Touch</h2>
              <p>
                Ready to go solar? Drop us a message and our energy advisor will call you within 24 hours —
                no obligations, just clarity.
              </p>
            </div>
          </div>

          <div className='row contact-cards-row'>

            {/* ── Left: Form card ── */}
            <div className='col-md-7 col-sm-12 contact-form-col'>
              <div className='contact-form-card'>
                <h3 className='form-card-title'>
                  <i className='fa fa-paper-plane-o'></i> Send Us a Message
                </h3>

                <form name='sentMessage' onSubmit={handleSubmit}>
                  <div className='row'>
                    <div className='col-sm-6'>
                      <div className='contact-field-group'>
                        <label htmlFor='name'><i className='fa fa-user'></i> Full Name</label>
                        <input
                          type='text' id='name' name='name'
                          className='contact-input'
                          placeholder='Ramesh Kumar'
                          required value={name} onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className='col-sm-6'>
                      <div className='contact-field-group'>
                        <label htmlFor='phone'><i className='fa fa-phone'></i> Phone Number</label>
                        <input
                          type='tel' id='phone' name='phone'
                          className={`contact-input${errors.phone ? ' input-error' : ''}`}
                          placeholder='+91 98765 43210'
                          required value={phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors.phone && <span className='field-error'><i className='fa fa-exclamation-circle'></i> {errors.phone}</span>}
                      </div>
                    </div>
                    <div className='col-sm-6'>
                      <div className='contact-field-group'>
                        <label htmlFor='email'><i className='fa fa-envelope-o'></i> Email Address</label>
                        <input
                          type='email' id='email' name='email'
                          className={`contact-input${errors.email ? ' input-error' : ''}`}
                          placeholder='you@example.com'
                          value={email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors.email && <span className='field-error'><i className='fa fa-exclamation-circle'></i> {errors.email}</span>}
                      </div>
                    </div>
                    <div className='col-sm-6'>
                      <div className='contact-field-group'>
                        <label htmlFor='bill_amount'><i className='fa fa-bolt'></i> Monthly Electricity Bill (₹)</label>
                        <input
                          type='number' id='bill_amount' name='bill_amount'
                          className='contact-input'
                          placeholder='e.g. 3500'
                          required value={bill_amount} onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className='col-sm-12'>
                      <div className='contact-field-group'>
                        <label htmlFor='message'><i className='fa fa-comment-o'></i> Message</label>
                        <textarea
                          id='message' name='message'
                          className='contact-input contact-textarea'
                          rows='4'
                          placeholder='Tell us about your property, requirements, or any questions…'
                          required value={message} onChange={handleChange}
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  {/* Status messages */}
                  {status === 'success' && (
                    <div className='contact-alert contact-alert-success'>
                      <i className='fa fa-whatsapp'></i>&nbsp;
                      WhatsApp opened with your details! If it didn't open, <a href={`https://wa.me/${WA_NUMBER}`} target='_blank' rel='noopener noreferrer'>click here</a>.
                    </div>
                  )}

                  <button
                    type='submit'
                    className='btn contact-submit-btn contact-submit-wa'
                  >
                    <i className='fa fa-whatsapp'></i> Send via WhatsApp
                  </button>
                </form>
              </div>
            </div>

            {/* ── Right: Info panel ── */}
            <div className='col-md-5 col-sm-12 contact-info-col'>
              <div className='contact-info-panel'>

                <div className='contact-info-block'>
                  <div className='contact-info-icon-wrap'>
                    <i className='fa fa-map-marker'></i>
                  </div>
                  <div>
                    <span className='contact-info-label'>Head Office</span>
                    <p>{props.data ? props.data.address : '—'}</p>
                  </div>
                </div>

                <div className='contact-info-block'>
                  <div className='contact-info-icon-wrap'>
                    <i className='fa fa-map-marker'></i>
                  </div>
                  <div>
                    <span className='contact-info-label'>Corporate Office</span>
                    <p>{props.data ? props.data.address2 : '—'}</p>
                  </div>
                </div>

                <div className='contact-info-block'>
                  <div className='contact-info-icon-wrap'>
                    <i className='fa fa-phone'></i>
                  </div>
                  <div>
                    <span className='contact-info-label'>Phone</span>
                    <p>
                      <a href={`tel:${props.data ? props.data.phone.replace(/\s/g,'') : ''}`}>
                        {props.data ? props.data.phone : '—'}
                      </a>
                    </p>
                  </div>
                </div>

                <div className='contact-info-block'>
                  <div className='contact-info-icon-wrap'>
                    <i className='fa fa-envelope-o'></i>
                  </div>
                  <div>
                    <span className='contact-info-label'>Email</span>
                    <p>
                      <a href={`mailto:${props.data ? props.data.email : ''}`}>
                        {props.data ? props.data.email : '—'}
                      </a>
                    </p>
                  </div>
                </div>

                <div className='contact-info-block'>
                  <div className='contact-info-icon-wrap'>
                    <i className='fa fa-clock-o'></i>
                  </div>
                  <div>
                    <span className='contact-info-label'>Working Hours</span>
                    <p>Mon – Sat: 9:00 AM – 6:00 PM</p>
                  </div>
                </div>

                {/* Social links */}
                <div className='contact-social'>
                  <a href={props.data ? props.data.facebook : '/'} className='contact-social-btn' title='Facebook'>
                    <i className='fa fa-facebook'></i>
                  </a>
                  <a href={props.data ? props.data.twitter : '/'} className='contact-social-btn' title='Twitter'>
                    <i className='fa fa-twitter'></i>
                  </a>
                  <a href={props.data ? props.data.youtube : '/'} className='contact-social-btn' title='YouTube'>
                    <i className='fa fa-youtube-play'></i>
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div id='footer'>
        <div className='container'>
          <div className='row footer-inner'>
            <div className='col-md-4 col-sm-6 col-xs-12 footer-brand'>
              <img src='img/newgen_logo.jpg' alt='NewGen Solar' className='footer-logo' />
              <span className='footer-brand-name'>NewGen Solar Tech</span>
              <p className='footer-tagline'>Powering India, one rooftop at a time.</p>
            </div>
            <div className='col-md-4 col-sm-6 col-xs-12 footer-links-col'>
              <h5 className='footer-col-title'>Quick Links</h5>
              <ul className='footer-links'>
                <li><a href='#features'>Why Solar</a></li>
                <li><a href='#why-us'>Why Us</a></li>
                <li><a href='#calculator'>Savings Calculator</a></li>
                <li><a href='#services'>Services</a></li>
                <li><a href='#offices'>Our Offices</a></li>
              </ul>
            </div>
            <div className='col-md-4 col-sm-12 col-xs-12 footer-cert-col'>
              <h5 className='footer-col-title'>Certifications</h5>
              <p className='footer-cert-text'>
                MNRE Empanelled &nbsp;|&nbsp; BIS Certified<br />
                25-Year Panel Warranty &nbsp;|&nbsp; ISO 9001
              </p>
              <p className='footer-cert-text' style={{marginTop: '10px'}}>
                <i className='fa fa-leaf' style={{color:'#5ca9fb', marginRight:'6px'}}></i>
                Committed to a carbon-neutral India by 2047.
              </p>
            </div>
          </div>
          <div className='footer-bottom'>
            <p>&copy; {new Date().getFullYear()} NewGen Solar Tech Pvt. Ltd. All rights reserved.</p>
            <VisitorCounter />
          </div>
        </div>
      </div>
    </div>
  )
}

